/**
 * Cliente del CRUD de Sources (Sprint A de B.6.d.10).
 *
 * Dos scopes hermanos:
 *  - `project_sources`: insumos de un proyecto. Endpoints `/projects/{id}/sources`
 *    y `/sources/{id}`.
 *  - `tenant_sources`: knowledge base del tenant. Endpoints `/tenant-sources`.
 *
 * En Sprint A es solo storage + listado + descarga + archive/delete.
 * Sin extracción de texto, sin embeddings, sin RAG. La integración con
 * AI agents llega en Sprint C.
 */

import { apiFetch, apiFetchBinary, apiFetchForm } from './client'

export type SourceOrigin =
  | 'email'
  | 'meeting_minutes'
  | 'transcript'
  | 'study'
  | 'regulation'
  | 'contract'
  | 'screenshot'
  | 'other'

export const SOURCE_ORIGINS: SourceOrigin[] = [
  'email',
  'meeting_minutes',
  'transcript',
  'study',
  'regulation',
  'contract',
  'screenshot',
  'other',
]

interface SourceBase {
  id: string
  tenant_id: string
  filename: string
  mime_type: string
  size_bytes: number
  blob_path: string
  content_hash: string
  origin: SourceOrigin
  tags: string[]
  description: string | null
  uploaded_by_user_id: string | null
  archived_at: string | null
  created_at: string
  updated_at: string
}

export interface ProjectSource extends SourceBase {
  project_id: string
  phase_id: string | null
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface TenantSource extends SourceBase {}

// ─── Project sources ──────────────────────────────────────────────────────
export interface ListProjectSourcesFilters {
  phase_id?: string | undefined
  origin?: SourceOrigin | undefined
  include_archived?: boolean | undefined
}

export async function listProjectSources(
  accessToken: string,
  projectId: string,
  filters?: ListProjectSourcesFilters,
): Promise<ProjectSource[]> {
  const params = new URLSearchParams()
  if (filters?.phase_id) params.set('phase_id', filters.phase_id)
  if (filters?.origin) params.set('origin', filters.origin)
  if (filters?.include_archived) params.set('include_archived', 'true')
  const qs = params.toString()
  return apiFetch<ProjectSource[]>(
    `/api/v1/projects/${projectId}/sources${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

export interface UploadProjectSourceBody {
  file: File
  origin: SourceOrigin
  phaseId?: string | undefined
  tags?: string[] | undefined
  description?: string | undefined
}

export async function uploadProjectSource(
  accessToken: string,
  projectId: string,
  body: UploadProjectSourceBody,
): Promise<ProjectSource> {
  const formData = new FormData()
  formData.append('file', body.file)
  formData.append('origin', body.origin)
  if (body.phaseId) formData.append('phase_id', body.phaseId)
  if (body.tags && body.tags.length > 0) {
    formData.append('tags', body.tags.join(','))
  }
  if (body.description) formData.append('description', body.description)
  return apiFetchForm<ProjectSource>(
    `/api/v1/projects/${projectId}/sources`,
    { accessToken, formData },
  )
}

export async function getProjectSource(
  accessToken: string,
  sourceId: string,
): Promise<ProjectSource> {
  return apiFetch<ProjectSource>(`/api/v1/sources/${sourceId}`, { accessToken })
}

/** Devuelve `{ blob, filename }`. El caller construye el download en el browser. */
export async function downloadProjectSource(
  accessToken: string,
  sourceId: string,
): Promise<{ blob: Blob; filename: string | null }> {
  return apiFetchBinary(`/api/v1/sources/${sourceId}/download`, { accessToken })
}

export async function archiveProjectSource(
  accessToken: string,
  sourceId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/sources/${sourceId}/archive`, {
    accessToken,
    method: 'PATCH',
  })
}

export async function deleteProjectSource(
  accessToken: string,
  sourceId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/sources/${sourceId}`, {
    accessToken,
    method: 'DELETE',
  })
}

// ─── Tenant sources ───────────────────────────────────────────────────────
export interface ListTenantSourcesFilters {
  origin?: SourceOrigin | undefined
  include_archived?: boolean | undefined
}

export async function listTenantSources(
  accessToken: string,
  filters?: ListTenantSourcesFilters,
): Promise<TenantSource[]> {
  const params = new URLSearchParams()
  if (filters?.origin) params.set('origin', filters.origin)
  if (filters?.include_archived) params.set('include_archived', 'true')
  const qs = params.toString()
  return apiFetch<TenantSource[]>(
    `/api/v1/tenant-sources${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

export interface UploadTenantSourceBody {
  file: File
  origin: SourceOrigin
  tags?: string[] | undefined
  description?: string | undefined
}

export async function uploadTenantSource(
  accessToken: string,
  body: UploadTenantSourceBody,
): Promise<TenantSource> {
  const formData = new FormData()
  formData.append('file', body.file)
  formData.append('origin', body.origin)
  if (body.tags && body.tags.length > 0) {
    formData.append('tags', body.tags.join(','))
  }
  if (body.description) formData.append('description', body.description)
  return apiFetchForm<TenantSource>(`/api/v1/tenant-sources`, {
    accessToken,
    formData,
  })
}

export async function getTenantSource(
  accessToken: string,
  sourceId: string,
): Promise<TenantSource> {
  return apiFetch<TenantSource>(`/api/v1/tenant-sources/${sourceId}`, {
    accessToken,
  })
}

export async function downloadTenantSource(
  accessToken: string,
  sourceId: string,
): Promise<{ blob: Blob; filename: string | null }> {
  return apiFetchBinary(`/api/v1/tenant-sources/${sourceId}/download`, {
    accessToken,
  })
}

export async function archiveTenantSource(
  accessToken: string,
  sourceId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/tenant-sources/${sourceId}/archive`, {
    accessToken,
    method: 'PATCH',
  })
}

export async function deleteTenantSource(
  accessToken: string,
  sourceId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/tenant-sources/${sourceId}`, {
    accessToken,
    method: 'DELETE',
  })
}
